// src/contexts/AccessibilityContext.tsx
import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

interface AccessibilityContextType {
  accessibilityMode: boolean;
}

const AccessibilityContext = createContext<
  AccessibilityContextType | undefined
>(undefined);

// Accessibility Mode is a per-user preference (see AuthContext.User),
// set on the /profile page. This context exists purely so existing
// consumers can keep calling useAccessibility() without reaching into
// useAuth() directly - it derives its value straight from the
// authenticated user and needs no fetch or event listener of its own.
export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const accessibilityMode = isAuthenticated
    ? (user?.accessibilityMode ?? false)
    : false;

  return (
    <AccessibilityContext.Provider value={{ accessibilityMode }}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error(
      'useAccessibility must be used within an AccessibilityProvider'
    );
  }
  return context;
};
